import React, { useState, useEffect } from 'react';
import { getBookings, updateBookingStatus } from '../lib/api';
import Analytics from '../utils/analytics';
import './IncomingRequests.css';

export default function IncomingRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const data = await getBookings('artisan');
            // Filter only PENDING_APPROVAL? Or backend does it? 
            // The prompt/UI implies "Incoming Requests", which usually means pending.
            // But let's show all for now or filter client side if needed. 
            // For now, let's filter for PENDING_APPROVAL to match "Incoming Requests" concept.
            const pending = data.filter(r => r.status === 'PENDING_APPROVAL');
            setRequests(pending);
        } catch (err) {
            console.error(err);
            showToast('Failed to load requests.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (booking, action) => {
        try {
            // Optimistic UI update
            setRequests(prev => prev.filter(r => r.bookingId !== booking.bookingId));

            // Call API (once backend is ready)
            await updateBookingStatus(booking.artisanId, booking.startTime, action);

            showToast(
                action === 'CONFIRMED' ? '✓ Request accepted!' : 'Request declined.',
                action === 'CONFIRMED' ? 'success' : 'info'
            );

            // Analytics: Track artisan job response
            const eventName = action === 'CONFIRMED' ? 'job_accepted' : 'job_declined';
            const timeToRespond = Math.floor((new Date() - new Date(booking.createdAt || Date.now())) / 1000);
            Analytics.pushCustomEvent(eventName, {
                booking_id: booking.bookingId,
                package_name: booking.serviceName,
                time_to_respond_seconds: timeToRespond
            });
        } catch (err) {
            showToast('Failed to update status. Please try again.', 'error');
            fetchRequests(); // Revert
        }
    };

    if (loading) return <div>Loading requests...</div>;

    return (
        <div className="incoming-requests-container">
            <h2>Incoming Requests</h2>
            {toast && (
                <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontWeight: 500,
                    animation: 'fadeIn 0.3s ease',
                    background: toast.type === 'success' ? '#dcfce7' : toast.type === 'error' ? '#fee2e2' : '#e0f2fe',
                    color: toast.type === 'success' ? '#15803d' : toast.type === 'error' ? '#b91c1c' : '#0369a1',
                    border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : toast.type === 'error' ? '#fecaca' : '#bae6fd'}`,
                }}>
                    {toast.message}
                </div>
            )}
            {requests.length === 0 ? (
                <p>No pending requests.</p>
            ) : (
                <div className="requests-grid">
                    {requests.map(req => (
                        <div key={req.bookingId} className="request-card">
                            <div className="req-header">
                                <span className="req-service">{req.serviceName}</span>
                                <span className="req-time">
                                    {new Date(req.startTime).toLocaleDateString()} @ {new Date(req.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div className="req-body">
                                <p><strong>Customer:</strong> {req.customerName}</p>
                                <p><strong>Location:</strong> {req.location?.address}</p>
                                {req.notes && <p className="req-notes">"{req.notes}"</p>}
                            </div>
                            <div className="req-actions">
                                <button
                                    className="btn-decline"
                                    onClick={() => handleAction(req, 'DECLINED')}
                                >
                                    Decline
                                </button>
                                <button
                                    className="btn-accept"
                                    onClick={() => handleAction(req, 'CONFIRMED')}
                                >
                                    Accept Request
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
